import { useCallback, useEffect, useRef, useState } from "react";
import { Subject } from "rxjs";
import { getUniqueId } from "@jugl-web/utils";
import type { InstagramMessageModel } from "../models/InstagramMessage/InstagramMessageModel";
import { useRestApiProvider } from "../../../RestApiProvider";
import type {
  UsePaginatedRequestHookParams,
  UsePaginatedRequestHookReturn,
} from "../../../types";

export const instagramMessages$ = new Subject<{
  type: "append";
  message: InstagramMessageModel;
}>();

export type UseInstagramMessagesParams = UsePaginatedRequestHookParams<{
  entityId: string;
  conversationId: string;
}>;

export type UseInstagramMessagesReturn =
  UsePaginatedRequestHookReturn<InstagramMessageModel>;

export const useInstagramMessages = ({
  entityId,
  conversationId,
  pageSize = 50,
  skip = false,
}: UseInstagramMessagesParams): UseInstagramMessagesReturn => {
  const { integrationsApi } = useRestApiProvider();
  const requestId = useRef<string>(undefined);

  const [messages, setMessages] = useState<InstagramMessageModel[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [fetchMessages] = integrationsApi.useLazyGetInstagramMessagesQuery();

  const loadMessages = useCallback(
    async (page: number) => {
      try {
        setIsError(false);
        setIsLoading(true);

        const newRequestId = getUniqueId();
        requestId.current = newRequestId;

        const nextYear = new Date().getFullYear() + 1;
        const from = `${nextYear}-01-01T00:00:00`;

        const result = await fetchMessages({
          entityId,
          params: {
            conv_id: conversationId,
            from,
            page_size: pageSize,
            page,
          },
        }).unwrap();

        if (requestId.current !== newRequestId) return;

        if (result) {
          setMessages((prev) => [...[...result.data].reverse(), ...prev]);
          setTotalPages(result.total_pages);
          setCurrentPage(page);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [entityId, conversationId, pageSize, fetchMessages]
  );

  const loadMore = useCallback(async () => {
    if (isLoading) return;
    if (totalPages !== null && currentPage >= totalPages) return;
    await loadMessages(currentPage + 1);
  }, [isLoading, currentPage, totalPages, loadMessages]);

  const reset = useCallback(() => {
    setMessages([]);
    setCurrentPage(1);
    setTotalPages(null);
    setIsError(false);
    loadMessages(1);
  }, [loadMessages]);

  const retry = useCallback(async () => {
    const pageToRetry =
      currentPage === 1 && messages.length === 0 ? 1 : currentPage + 1;
    await loadMessages(pageToRetry);
  }, [currentPage, messages.length, loadMessages]);

  // Reset and load initial messages when conversationId changes
  useEffect(() => {
    if (skip) return;
    reset();
  }, [conversationId, reset, skip]);

  // Listen to Subject for real-time message appending
  useEffect(() => {
    const subscription = instagramMessages$.subscribe((action) => {
      if (action.type === "append") {
        const { message } = action;

        // Only append messages for current conversation
        if (message.conv_id !== conversationId) return;

        setMessages((prev) => {
          // Check if message already exists (deduplicate by msg_id)
          const existingIndex = prev.findIndex(
            (m) => m.msg_id === message.msg_id
          );

          if (existingIndex >= 0) {
            // Update existing message
            const newMessages = [...prev];
            newMessages[existingIndex] = message;
            return newMessages;
          }

          // Append new message to end
          return [...prev, message];
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [conversationId]);

  const hasMore = totalPages !== null && currentPage < totalPages;
  const isLoadingMore = isLoading && currentPage > 1;
  const isLoadingMoreError =
    isError && (currentPage > 1 || messages.length === 0);

  return {
    data: messages,
    isLoading,
    isLoadingMore,
    isError,
    isLoadingMoreError,
    hasMore,
    reset,
    loadMore,
    retry,
  };
};
